import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PaletteIcon from "@mui/icons-material/Palette";
import ReplyIcon from "@mui/icons-material/Reply";
import ShieldIcon from "@mui/icons-material/Shield";
import StraightenIcon from "@mui/icons-material/Straighten";
import TimerIcon from "@mui/icons-material/Timer";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const RAW_API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const TURQUOISE = "#00B4A6";
const TURQUOISE_DARK = "#00796B";
const TURQUOISE_MIST = "#DDFBF8";

const GOLD = "#D4AF37";
const GOLD_DARK = "#8A6500";
const GOLD_MIST = "#FFF4CF";

const BURGUNDY = "#7A1232";
const BURGUNDY_DARK = "#4A071D";
const BURGUNDY_MIST = "#FBEAF0";

const INK = "#071313";
const MUTED = "#60706D";
const WHITE = "#ffffff";
const LINE = "rgba(7, 19, 19, 0.10)";

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";

function buildApiUrl(path) {
  const base = RAW_API_BASE_URL.replace(/\/+$/, "");
  const cleanPath = path.replace(/^\/+/, "");

  if (!base) {
    return `/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
  }

  if (base.endsWith("/api")) {
    return `${base}/${cleanPath.replace(/^api\//, "")}`;
  }

  return `${base}/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
}

function forceHttpsUrl(url) {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (cleanUrl.startsWith("http://")) {
    return `https://${cleanUrl.slice("http://".length)}`;
  }

  return cleanUrl;
}

function moneyNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === "") return fallback;

  const clean = String(value).replace("£", "").replace(",", "").trim();
  const number = Number(clean);

  if (!Number.isFinite(number)) return fallback;

  return number;
}

function moneyLabel(value, fallback = "TBC") {
  if (value === null || value === undefined || value === "") return fallback;

  const number = moneyNumber(value, null);

  if (number === null) return String(value);

  return `£${number.toFixed(2)}`;
}

function getArtworkTitle(data) {
  return (
    data?.painting_title ||
    data?.artwork_title ||
    data?.painting?.title ||
    "Untitled artwork"
  );
}

function getArtworkImageUrl(data) {
  return forceHttpsUrl(
    data?.painting_image_url ||
      data?.artwork_image_url ||
      data?.painting?.image_url ||
      data?.painting?.image ||
      ""
  );
}

function getArtworkArtist(data) {
  return (
    data?.painting_artist_name ||
    data?.artist_name ||
    data?.painting_buyer_facing_artist_name ||
    data?.buyer_facing_artist_name ||
    data?.painting?.buyer_facing_artist_name ||
    data?.painting?.artist_name ||
    "Artist"
  );
}

function getArtworkMedium(data) {
  return (
    data?.painting_medium ||
    data?.artwork_medium ||
    data?.painting?.medium ||
    "Artwork"
  );
}

function getArtworkSize(data) {
  return (
    data?.painting_size_label ||
    data?.artwork_size_label ||
    data?.painting?.size_label ||
    data?.painting?.size ||
    "Size TBC"
  );
}

function getArtworkCategory(data) {
  const raw =
    data?.painting_artwork_category ||
    data?.artwork_category ||
    data?.painting?.artwork_category ||
    "artwork";

  const labels = {
    painting: "Painting",
    print: "Print",
    sculpture: "Sculpture",
    decorative_object: "Decorative Object",
    artwork: "Artwork",
  };

  return labels[raw] || raw;
}

function getBuyerName(data) {
  return data?.buyer_name || data?.buyer_display_name || "Buyer";
}

function getOfferFloor(data) {
  return (
    data?.painting_minimum_offer_label ||
    data?.painting_current_offer_floor ||
    data?.painting?.minimum_offer_label ||
    moneyLabel(data?.painting?.current_offer_minimum_gbp, "Offer floor TBC")
  );
}

function getStatusLabel(status) {
  const labels = {
    pending: "Waiting for artist",
    countered: "Counter sent",
    buyer_countered: "Buyer countered",
    accepted: "Accepted",
    payment_pending: "Payment pending",
    declined: "Declined",
    expired: "Expired",
    withdrawn: "Withdrawn",
    paid: "Paid",
    cancelled: "Cancelled",
  };

  return labels[status] || status || "Pending";
}

function isFinalStatus(status) {
  return ["accepted", "payment_pending", "declined", "expired", "withdrawn", "paid", "cancelled"].includes(
    status
  );
}

function formatDateTime(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getTimeLeft(value) {
  if (!value) return "";

  const deadline = new Date(value).getTime();

  if (Number.isNaN(deadline)) return "";

  const diff = deadline - Date.now();

  if (diff <= 0) return "Expired";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff / (1000 * 60)) % 60);

  if (hours <= 0) return `${minutes}m left`;

  return `${hours}h ${minutes}m left`;
}

const PageShell = styled(Box)({
  minHeight: "100vh",
  background:
    `radial-gradient(circle at 0% 0%, ${alpha(TURQUOISE, 0.16)}, transparent 30%),
     radial-gradient(circle at 100% 0%, ${alpha(GOLD, 0.15)}, transparent 28%),
     radial-gradient(circle at 100% 78%, ${alpha(BURGUNDY, 0.12)}, transparent 26%),
     linear-gradient(180deg, #fbfffe 0%, #f7fffd 48%, #fffaf0 100%)`,
  color: INK,
  fontFamily: BODY_FONT,
});

const Panel = styled(Paper)({
  borderRadius: 28,
  border: `1px solid ${LINE}`,
  background: alpha(WHITE, 0.94),
  boxShadow: "0 18px 60px rgba(7, 19, 19, 0.07)",
  overflow: "hidden",
});

const DarkPanel = styled(Paper)({
  borderRadius: 30,
  background:
    `linear-gradient(135deg, ${BURGUNDY_DARK} 0%, #092b28 56%, #171105 100%)`,
  color: WHITE,
  border: `1px solid ${alpha(WHITE, 0.12)}`,
  boxShadow: "0 24px 70px rgba(7, 19, 19, 0.22)",
  overflow: "hidden",
});

const PrimaryButton = styled(Button)({
  textTransform: "none",
  fontWeight: 900,
  borderRadius: 999,
  padding: "12px 20px",
  background: `linear-gradient(135deg, ${TURQUOISE} 0%, ${GOLD} 100%)`,
  color: WHITE,
  boxShadow: `0 14px 30px ${alpha(TURQUOISE, 0.22)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${TURQUOISE_DARK} 0%, ${GOLD_DARK} 100%)`,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: alpha(TURQUOISE, 0.35),
    color: alpha(WHITE, 0.82),
  },
});

const BurgundyButton = styled(Button)({
  textTransform: "none",
  fontWeight: 900,
  borderRadius: 999,
  padding: "12px 20px",
  background: `linear-gradient(135deg, ${BURGUNDY} 0%, ${BURGUNDY_DARK} 100%)`,
  color: WHITE,
  boxShadow: `0 14px 30px ${alpha(BURGUNDY, 0.18)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${BURGUNDY_DARK} 0%, ${BURGUNDY} 100%)`,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: alpha(BURGUNDY, 0.32),
    color: alpha(WHITE, 0.82),
  },
});

const SoftButton = styled(Button)({
  textTransform: "none",
  fontWeight: 850,
  borderRadius: 999,
  padding: "10px 16px",
  color: TURQUOISE_DARK,
  border: `1px solid ${alpha(TURQUOISE, 0.32)}`,
  background: WHITE,
  "&:hover": {
    background: TURQUOISE_MIST,
    borderColor: TURQUOISE,
  },
});

const MinimalInput = styled(TextField)({
  "& label": {
    color: alpha(INK, 0.58),
  },
  "& label.Mui-focused": {
    color: TURQUOISE_DARK,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 18,
    background: WHITE,
    "& fieldset": {
      borderColor: LINE,
    },
    "&:hover fieldset": {
      borderColor: alpha(TURQUOISE, 0.45),
    },
    "&.Mui-focused fieldset": {
      borderColor: TURQUOISE,
    },
  },
});

const MetaBox = ({ icon, label, value, tone = "turquoise" }) => {
  const toneStyles = {
    turquoise: {
      background: TURQUOISE_MIST,
      color: TURQUOISE_DARK,
    },
    gold: {
      background: GOLD_MIST,
      color: GOLD_DARK,
    },
    burgundy: {
      background: BURGUNDY_MIST,
      color: BURGUNDY,
    },
  };

  const selectedTone = toneStyles[tone] || toneStyles.turquoise;

  return (
    <Box
      sx={{
        p: 1.6,
        borderRadius: 4,
        bgcolor: selectedTone.background,
        border: `1px solid ${alpha(selectedTone.color, 0.12)}`,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            bgcolor: WHITE,
            color: selectedTone.color,
            flex: "0 0 auto",
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: alpha(INK, 0.56),
              fontSize: 12,
              fontWeight: 850,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              color: INK,
              fontWeight: 950,
              overflowWrap: "anywhere",
            }}
          >
            {value || "TBC"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default function ArtistOfferResponsePage() {
  const { offerId, token } = useParams();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());

  const [artistNote, setArtistNote] = useState("");
  const [counterAmount, setCounterAmount] = useState("");
  const [makeNewMinimum, setMakeNewMinimum] = useState(false);

  const detailUrl = useMemo(
    () => buildApiUrl(`room-offers/${offerId}/artist-response/${token}/`),
    [offerId, token]
  );

  const actionUrl = (action) =>
    buildApiUrl(`room-offers/${offerId}/artist-response/${token}/${action}/`);

  const artworkTitle = getArtworkTitle(offer);
  const artworkImageUrl = getArtworkImageUrl(offer);
  const artworkArtist = getArtworkArtist(offer);
  const artworkMedium = getArtworkMedium(offer);
  const artworkSize = getArtworkSize(offer);
  const artworkCategory = getArtworkCategory(offer);

  const buyerName = getBuyerName(offer);
  const offerAmount = moneyNumber(offer?.amount_gbp, 0);
  const counterAmountNumber = moneyNumber(offer?.counter_amount_gbp, 0);
  const acceptedAmount = moneyNumber(offer?.accepted_amount_gbp, 0);

  const offerAmountLabel = moneyLabel(offer?.amount_gbp, "Offer TBC");
  const counterAmountLabel = moneyLabel(offer?.counter_amount_gbp, "");
  const acceptedAmountLabel = moneyLabel(offer?.accepted_amount_gbp, "");

  const postageLabel =
    offer?.postage_fee_label ||
    moneyLabel(offer?.postage_fee_gbp, "Postage TBC");

  const totalLabel =
    offer?.total_due_label ||
    moneyLabel(offer?.total_due_gbp, "Total TBC");

  const offerFloor = getOfferFloor(offer);
  const statusLabel = getStatusLabel(offer?.status);
  const expiresAtLabel = formatDateTime(offer?.expires_at);
  const timeLeft = getTimeLeft(offer?.expires_at);
  const countersLeft =
    offer?.remaining_artist_counter_chances ??
    Math.max(
      0,
      Number(offer?.max_artist_counter_chances || 3) -
        Number(offer?.artist_counter_count || 0)
    );

  const buyerOffersUsed = offer?.buyer_offer_count ?? 1;
  const artistCountersUsed = offer?.artist_counter_count ?? 0;

  const finalStatus = isFinalStatus(offer?.status);
  const expiredByClock = offer?.expires_at && getTimeLeft(offer.expires_at) === "Expired";
  const actionsDisabled = Boolean(actionLoading) || finalStatus || expiredByClock;

  useEffect(() => {
    document.title = "Respond to Offer | Art For My Room";

    const description =
      "Secure artist offer response page for Art For My Room.";

    let meta = document.querySelector('meta[name="description"]');

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", description);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchOffer = async () => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const response = await fetch(detailUrl, {
          headers: {
            Accept: "application/json",
          },
        });

        let data = {};

        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (!response.ok) {
          throw new Error(
            data.error ||
              data.message ||
              "This secure offer link could not be opened."
          );
        }

        setOffer(data);

        const suggestedCounter =
          data.counter_amount_gbp ||
          data.suggested_counter_amount_gbp ||
          data.amount_gbp ||
          "";

        setCounterAmount(
          suggestedCounter
            ? moneyNumber(suggestedCounter, 0).toFixed(2)
            : ""
        );
      } catch (err) {
        setError(err.message || "This offer could not be loaded.");
      } finally {
        setLoading(false);
      }
    };

    if (offerId && token) {
      fetchOffer();
    } else {
      setLoading(false);
      setError("The offer link is missing information.");
    }
  }, [detailUrl, offerId, token]);

  const refreshOffer = async () => {
    try {
      const response = await fetch(detailUrl, {
        headers: {
          Accept: "application/json",
        },
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok) {
        setOffer(data);
      }
    } catch {
      // Keep the action message visible if refresh fails.
    }
  };

  const submitAction = async (action) => {
    if (!offerId || !token) {
      setError("The offer link is missing information.");
      return;
    }

    if (actionsDisabled) {
      setError("This offer is no longer open for response.");
      return;
    }

    const payload = {
      artist_note: artistNote.trim(),
    };

    if (action === "counter") {
      const amount = moneyNumber(counterAmount, null);

      if (amount === null || amount <= 0) {
        setError("Enter a valid counter-offer amount.");
        return;
      }

      if (amount <= offerAmount) {
        setError(`Counter-offer must be above the buyer offer of £${offerAmount.toFixed(2)}.`);
        return;
      }

      if (Number(countersLeft) <= 0) {
        setError("You have used all 3 counter-offer chances.");
        return;
      }

      payload.counter_amount_gbp = amount.toFixed(2);
    }

    if (action === "decline") {
      payload.make_new_minimum = Boolean(makeNewMinimum);
    }

    setActionLoading(action);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(actionUrl(action), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.message ||
            data.detail ||
            "The response could not be saved."
        );
      }

      const messages = {
        accept: "Accepted. The buyer will receive the payment link.",
        counter: "Counter-offer sent. The buyer has 24 hours to respond.",
        decline: makeNewMinimum
          ? "Declined. This amount can become the new minimum."
          : "Declined. The buyer will be notified.",
      };

      setSuccess(data.message || messages[action] || "Response saved.");
      await refreshOffer();
    } catch (err) {
      setError(err.message || "The response could not be saved.");
    } finally {
      setActionLoading("");
    }
  };

  return (
    <PageShell>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <DarkPanel elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} md={8}>
                <Chip
                  icon={<LocalOfferIcon />}
                  label="Secure offer"
                  sx={{
                    mb: 2,
                    bgcolor: alpha(WHITE, 0.1),
                    color: WHITE,
                    border: `1px solid ${alpha(WHITE, 0.16)}`,
                    fontWeight: 900,
                    "& .MuiChip-icon": {
                      color: GOLD,
                    },
                  }}
                />

                <Typography
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontSize: { xs: "2.2rem", md: "4rem" },
                    fontWeight: 950,
                    letterSpacing: "-0.07em",
                    lineHeight: 0.96,
                  }}
                >
                  Respond to this offer
                </Typography>

                <Typography
                  sx={{
                    mt: 1.5,
                    color: alpha(WHITE, 0.72),
                    lineHeight: 1.75,
                    maxWidth: 720,
                  }}
                >
                  Accept, counter-offer or decline. The buyer stays inside Art For My Room.
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <Box
                    sx={{
                      p: 1.4,
                      borderRadius: 4,
                      bgcolor: alpha(WHITE, 0.08),
                      border: `1px solid ${alpha(WHITE, 0.14)}`,
                    }}
                  >
                    <Typography sx={{ color: alpha(WHITE, 0.62), fontSize: 12, fontWeight: 850 }}>
                      Artist
                    </Typography>
                    <Typography sx={{ color: WHITE, fontWeight: 950 }}>
                      {artworkArtist}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.4,
                      borderRadius: 4,
                      bgcolor: alpha(GOLD, 0.13),
                      border: `1px solid ${alpha(GOLD, 0.18)}`,
                    }}
                  >
                    <Typography sx={{ color: alpha(WHITE, 0.62), fontSize: 12, fontWeight: 850 }}>
                      Offer
                    </Typography>
                    <Typography sx={{ color: WHITE, fontWeight: 950 }}>
                      {offerAmountLabel}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </DarkPanel>

          {loading && (
            <Panel sx={{ p: 5, textAlign: "center" }} elevation={0}>
              <CircularProgress sx={{ color: TURQUOISE_DARK, mb: 2 }} />
              <Typography sx={{ fontWeight: 950 }}>
                Opening offer...
              </Typography>
            </Panel>
          )}

          {!loading && error && !offer && (
            <Panel sx={{ p: { xs: 3, md: 4 } }} elevation={0}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>

              <SoftButton
                component={RouterLink}
                to="/"
                startIcon={<HomeRoundedIcon />}
              >
                Go to Art For My Room
              </SoftButton>
            </Panel>
          )}

          {!loading && offer && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Panel elevation={0} sx={{ height: "100%" }}>
                  {artworkImageUrl ? (
                    <Box
                      component="img"
                      src={artworkImageUrl}
                      alt={artworkTitle}
                      sx={{
                        width: "100%",
                        height: { xs: 310, md: 430 },
                        display: "block",
                        objectFit: "cover",
                        bgcolor: TURQUOISE_MIST,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: { xs: 310, md: 430 },
                        display: "grid",
                        placeItems: "center",
                        bgcolor: TURQUOISE_MIST,
                      }}
                    >
                      <PaletteIcon sx={{ fontSize: 70, color: TURQUOISE_DARK }} />
                    </Box>
                  )}

                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={1.7}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          label={artworkCategory}
                          sx={{
                            bgcolor: BURGUNDY_MIST,
                            color: BURGUNDY,
                            fontWeight: 950,
                          }}
                        />

                        <Chip
                          label={getStatusLabel(offer.status)}
                          sx={{
                            bgcolor: offer.status === "accepted" || offer.status === "paid"
                              ? TURQUOISE_MIST
                              : GOLD_MIST,
                            color: offer.status === "accepted" || offer.status === "paid"
                              ? TURQUOISE_DARK
                              : GOLD_DARK,
                            fontWeight: 950,
                          }}
                        />
                      </Stack>

                      <Box>
                        <Typography
                          sx={{
                            fontFamily: HEADING_FONT,
                            fontWeight: 950,
                            fontSize: { xs: "1.55rem", md: "2.1rem" },
                            letterSpacing: "-0.045em",
                            lineHeight: 1.05,
                          }}
                        >
                          {artworkTitle}
                        </Typography>

                        <Typography sx={{ color: MUTED, mt: 1 }}>
                          {artworkMedium} · {artworkSize}
                        </Typography>
                      </Box>

                      <Divider />

                      <Grid container spacing={1.3}>
                        <Grid item xs={6}>
                          <MetaBox
                            icon={<LocalOfferIcon />}
                            label="Buyer offer"
                            value={offerAmountLabel}
                            tone="gold"
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <MetaBox
                            icon={<TrendingUpIcon />}
                            label="Offer floor"
                            value={offerFloor}
                            tone="turquoise"
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <MetaBox
                            icon={<StraightenIcon />}
                            label="Postage"
                            value={postageLabel}
                            tone="turquoise"
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <MetaBox
                            icon={<ShieldIcon />}
                            label="Total"
                            value={totalLabel}
                            tone="burgundy"
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </CardContent>
                </Panel>
              </Grid>

              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  <Panel sx={{ p: { xs: 2.5, md: 3 } }} elevation={0}>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.2}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: HEADING_FONT,
                              fontWeight: 950,
                              fontSize: "1.65rem",
                              letterSpacing: "-0.04em",
                            }}
                          >
                            Offer from {buyerName}
                          </Typography>

                          <Typography sx={{ color: MUTED }}>
                            Status: {statusLabel}
                          </Typography>
                        </Box>

                        <Chip
                          icon={<TimerIcon />}
                          label={timeLeft || "No deadline"}
                          sx={{
                            bgcolor: expiredByClock ? BURGUNDY_MIST : GOLD_MIST,
                            color: expiredByClock ? BURGUNDY : GOLD_DARK,
                            fontWeight: 950,
                          }}
                        />
                      </Stack>

                      <Grid container spacing={1.3}>
                        <Grid item xs={12} sm={4}>
                          <MetaBox
                            icon={<LocalOfferIcon />}
                            label="Buyer offers"
                            value={`${buyerOffersUsed}/3`}
                            tone="turquoise"
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <MetaBox
                            icon={<ReplyIcon />}
                            label="Artist counters"
                            value={`${artistCountersUsed}/3`}
                            tone="burgundy"
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <MetaBox
                            icon={<TimerIcon />}
                            label="Deadline"
                            value={expiresAtLabel || "24 hours"}
                            tone="gold"
                          />
                        </Grid>
                      </Grid>

                      {offer.message && (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 4,
                            bgcolor: "#fbfffe",
                            border: `1px solid ${LINE}`,
                          }}
                        >
                          <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 850, mb: 0.5 }}>
                            Buyer message
                          </Typography>
                          <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                            {offer.message}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Panel>

                  <Panel sx={{ p: { xs: 2.5, md: 3 } }} elevation={0}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: HEADING_FONT,
                            fontWeight: 950,
                            fontSize: "1.65rem",
                            letterSpacing: "-0.04em",
                          }}
                        >
                          Your action
                        </Typography>

                        <Typography sx={{ color: MUTED }}>
                          Choose one clear response.
                        </Typography>
                      </Box>

                      {error && (
                        <Alert severity="error" onClose={() => setError("")}>
                          {error}
                        </Alert>
                      )}

                      {success && (
                        <Alert severity="success" onClose={() => setSuccess("")}>
                          {success}
                        </Alert>
                      )}

                      {finalStatus && (
                        <Alert severity="warning">
                          This offer already has a final status: {statusLabel}.
                        </Alert>
                      )}

                      {expiredByClock && !finalStatus && (
                        <Alert severity="warning">
                          This offer link has expired. The buyer must make a new offer.
                        </Alert>
                      )}

                      <MinimalInput
                        fullWidth
                        multiline
                        minRows={3}
                        label="Note to buyer, optional"
                        value={artistNote}
                        onChange={(event) => setArtistNote(event.target.value)}
                      />

                      <Grid container spacing={1.4}>
                        <Grid item xs={12} md={6}>
                          <MinimalInput
                            fullWidth
                            label="Counter-offer"
                            value={counterAmount}
                            onChange={(event) => setCounterAmount(event.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">£</InputAdornment>
                              ),
                            }}
                            helperText={`${countersLeft} counter chance${Number(countersLeft) === 1 ? "" : "s"} left`}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Card
                            variant="outlined"
                            onClick={() => setMakeNewMinimum((current) => !current)}
                            sx={{
                              height: "100%",
                              borderRadius: 4,
                              cursor: "pointer",
                              borderColor: makeNewMinimum ? BURGUNDY : LINE,
                              bgcolor: makeNewMinimum ? BURGUNDY_MIST : WHITE,
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Stack direction="row" spacing={1.2} alignItems="center">
                                {makeNewMinimum ? (
                                  <CheckCircleIcon sx={{ color: BURGUNDY }} />
                                ) : (
                                  <CloseIcon sx={{ color: alpha(INK, 0.35) }} />
                                )}

                                <Box>
                                  <Typography sx={{ fontWeight: 950 }}>
                                    Raise minimum if declined
                                  </Typography>
                                  <Typography sx={{ color: MUTED, fontSize: 13 }}>
                                    New minimum: {offerAmountLabel}
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <PrimaryButton
                          fullWidth
                          startIcon={
                            actionLoading === "accept" ? (
                              <CircularProgress size={17} color="inherit" />
                            ) : (
                              <CheckCircleIcon />
                            )
                          }
                          disabled={actionsDisabled}
                          onClick={() => submitAction("accept")}
                        >
                          Accept
                        </PrimaryButton>

                        <BurgundyButton
                          fullWidth
                          startIcon={
                            actionLoading === "counter" ? (
                              <CircularProgress size={17} color="inherit" />
                            ) : (
                              <ReplyIcon />
                            )
                          }
                          disabled={actionsDisabled}
                          onClick={() => submitAction("counter")}
                        >
                          Counter
                        </BurgundyButton>

                        <SoftButton
                          fullWidth
                          startIcon={
                            actionLoading === "decline" ? (
                              <CircularProgress size={17} color="inherit" />
                            ) : (
                              <CloseIcon />
                            )
                          }
                          disabled={actionsDisabled}
                          onClick={() => submitAction("decline")}
                        >
                          Decline
                        </SoftButton>
                      </Stack>

                      <Typography sx={{ color: MUTED, fontSize: 12, lineHeight: 1.6 }}>
                        Negotiation is limited to 3 buyer offers and 3 artist counters.
                        Direct contact details stay off-platform.
                      </Typography>
                    </Stack>
                  </Panel>

                  {(counterAmountLabel || acceptedAmountLabel) && (
                    <Panel sx={{ p: 2.5 }} elevation={0}>
                      <Stack spacing={1}>
                        {counterAmountLabel && (
                          <Typography sx={{ color: MUTED }}>
                            Last counter-offer:{" "}
                            <Box component="span" sx={{ color: INK, fontWeight: 950 }}>
                              {counterAmountLabel}
                            </Box>
                          </Typography>
                        )}

                        {acceptedAmount > 0 && (
                          <Typography sx={{ color: MUTED }}>
                            Accepted amount:{" "}
                            <Box component="span" sx={{ color: INK, fontWeight: 950 }}>
                              {acceptedAmountLabel}
                            </Box>
                          </Typography>
                        )}
                      </Stack>
                    </Panel>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}
        </Stack>
      </Container>
    </PageShell>
  );
}
